import React, { useMemo, useState } from 'react';

import { Popover } from '@mui/material';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Card, CardHeader } from 'shared/mui/Card';
import { DEFAULT_SHORTCUTS } from 'shared/mui/DatePicker/components/DatePickerShortcuts/constants/shortcuts';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { DeleteDialog } from 'shared/mui/Dialogs';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFavoriteFilters } from 'shared/mui/Filter/FilterSaveFavorite/hooks/useFavoriteFilters';
import { PopoverProps } from 'shared/mui/Popover/Popover';
import { truncateString } from 'utils/truncateString';

import { FilterFavoritePopoverItem } from './FilterFavoritePopoverItem/FilterFavoritePopoverItem';

export type FilterFavoritePopoverProps = {
  name: string | undefined;
  setName: (v: string | undefined) => void;
  dateFields?: string[];
} & PopoverProps;

export const FilterFavoritePopover: React.FC<FilterFavoritePopoverProps> = ({
  name,
  setName,
  dateFields = ['appliedDate'],
  ...props
}) => {
  const translate = useTypedTranslate();
  const notify = useNotify();

  const [open, setOpen] = useState(false);

  const { setAppliedFilter } = useFilterContext();
  const form = useFormContext();

  const { favorites, removeFavoriteFilters } = useFavoriteFilters();

  const onClickFavorite = (name: string) => {
    const currentFavorite = favorites?.find((favorite) => favorite?.name === name);

    if (!currentFavorite || !form) {
      return;
    }

    const values = currentFavorite.values || {};
    const dateField =
      dateFields.find((field) => values[field]?.[0] || values[field]?.[1]) || dateFields[0];
    const timezone = values.timezone || DEFAULT_TIMEZONE;

    const shortcut = DEFAULT_SHORTCUTS(timezone)
      .flat(1)
      .find((shortcut) => shortcut?.alias === currentFavorite?.period);

    const formattedValues = {
      ...values,
      timezone,
      ...(shortcut && { [dateField]: shortcut.getValue() }),
    };

    for (const [key, value] of Object.entries(formattedValues)) {
      form.setValue(key, value, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setAppliedFilter(key, value);
    }
  };

  const sortedFavorites = useMemo(() => {
    return [...favorites].sort((a, b) => (a.favorite === b.favorite ? 0 : a.favorite ? -1 : 1));
  }, [favorites]);

  return (
    <Popover
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      disableAutoFocus={true}
      disableScrollLock={true}
      {...props}
    >
      <Card>
        <CardHeader
          sx={{ padding: '8px 16px', borderBottom: '1px solid', borderColor: 'divider' }}
          title={translate('app.filter.favorite')}
        />
        <Box
          sx={{
            height: '300px',
            overflowY: 'auto',
          }}
        >
          {sortedFavorites?.map((favorite) => {
            return (
              <FilterFavoritePopoverItem
                favorite={favorite.favorite}
                key={favorite.name}
                name={favorite.name}
                onClick={onClickFavorite}
                onRemove={(name: string) => {
                  setOpen(true);
                  setName(name);
                }}
                period={favorite.period}
              />
            );
          })}
        </Box>
        <DeleteDialog
          details={`${translate('app.filter.filterSettings')} ${truncateString(name || '', 33)} ${translate('app.filter.willBeDeletedFromLocalStorage')}.
`}
          onClose={() => {
            setOpen(false);
          }}
          onDelete={() => {
            name && removeFavoriteFilters(name);
            setOpen(false);
            if (sortedFavorites.length === 1 && props.onClose) {
              props.onClose({}, 'backdropClick');
            }
            notify(translate('app.filter.presetDeleted', { name }), {
              type: 'success',
            });
          }}
          open={open}
          title={translate('app.filter.deleteFilterSettings')}
        />
      </Card>
    </Popover>
  );
};
